import { useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, Input, Select, Table, Badge, Modal, LoadingSpinner } from '@/components/common';
import {
  useCountries,
  useCreateCountry,
  useUpdateCountry,
  useDeleteCountry,
  useActivateCountry,
  usePauseCountry,
  useBlockCountry,
} from '@/hooks/useGeography';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { Country, CountryRequest } from '@/services/geography';

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'blocked', label: 'Blocked' },
];

const statusColors: Record<string, 'success' | 'warning' | 'error'> = {
  active: 'success',
  paused: 'warning',
  blocked: 'error',
};

export function CountriesPage() {
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isLoading } = useCountries(page, { search: debouncedSearch, status: statusFilter || undefined });
  const createMutation = useCreateCountry();
  const updateMutation = useUpdateCountry();
  const deleteMutation = useDeleteCountry();
  const activateMutation = useActivateCountry();
  const pauseMutation = usePauseCountry();
  const blockMutation = useBlockCountry();

  const [formData, setFormData] = useState<CountryRequest>({
    name: '',
    code: '',
    code_alpha2: '',
    phone_code: '',
    currency_code: 'INR',
    currency_symbol: '₹',
    timezone: 'Asia/Kolkata',
    status: 'active',
    regulatory_body: '',
    tax_rate: 18,
    kyc_required: true,
  });

  const handleOpenModal = (country?: Country) => {
    if (country) {
      setEditingCountry(country);
      setFormData({
        name: country.name,
        code: country.code,
        code_alpha2: country.code_alpha2,
        phone_code: country.phone_code,
        currency_code: country.currency_code,
        currency_symbol: country.currency_symbol,
        timezone: country.timezone,
        status: country.status,
        regulatory_body: country.regulatory_body || '',
        tax_rate: country.tax_rate || 18,
        kyc_required: country.kyc_required ?? true,
      });
    } else {
      setEditingCountry(null);
      setFormData({
        name: '',
        code: '',
        code_alpha2: '',
        phone_code: '',
        currency_code: 'INR',
        currency_symbol: '₹',
        timezone: 'Asia/Kolkata',
        status: 'active',
        regulatory_body: '',
        tax_rate: 18,
        kyc_required: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCountry) {
      await updateMutation.mutateAsync({ id: editingCountry.id, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setIsModalOpen(false);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const columns = [
    {
      key: 'name',
      header: 'Country',
      render: (country: Country) => (
        <div>
          <p className="font-medium text-gray-900">{country.name}</p>
          <p className="text-sm text-gray-500">{country.code} / {country.code_alpha2}</p>
        </div>
      ),
    },
    {
      key: 'currency',
      header: 'Currency',
      render: (country: Country) => (
        <span>{country.currency_symbol} {country.currency_code}</span>
      ),
    },
    {
      key: 'phone_code',
      header: 'Phone Code',
      render: (country: Country) => country.phone_code,
    },
    {
      key: 'timezone',
      header: 'Timezone',
      render: (country: Country) => country.timezone,
    },
    {
      key: 'states_count',
      header: 'States',
      render: (country: Country) => country.states_count || 0,
    },
    {
      key: 'status',
      header: 'Status',
      render: (country: Country) => (
        <Badge variant={statusColors[country.status]}>
          {country.status.charAt(0).toUpperCase() + country.status.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (country: Country) => (
        <div className="flex items-center gap-2">
          {country.status !== 'active' && (
            <button
              onClick={() => activateMutation.mutate(country.id)}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="Activate"
            >
              <PlayIcon className="w-4 h-4" />
            </button>
          )}
          {country.status === 'active' && (
            <button
              onClick={() => pauseMutation.mutate(country.id)}
              className="p-1 text-yellow-600 hover:bg-yellow-50 rounded"
              title="Pause"
            >
              <PauseIcon className="w-4 h-4" />
            </button>
          )}
          {country.status !== 'blocked' && (
            <button
              onClick={() => blockMutation.mutate(country.id)}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Block"
            >
              <NoSymbolIcon className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => handleOpenModal(country)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteConfirm(country.id)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Countries"
        subtitle="Manage countries and their configurations"
        actions={
          <Button onClick={() => handleOpenModal()}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Country
          </Button>
        }
      />

      <Card>
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.results || []}
          emptyMessage="No countries found"
          rowKey={(country) => country.id}
        />

        {data && data.count > 20 && (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.count)} of {data.count}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!data.next}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingCountry ? 'Edit Country' : 'Add Country'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Country Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              label="ISO Code (Alpha-3)"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              maxLength={3}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="ISO Code (Alpha-2)"
              value={formData.code_alpha2}
              onChange={(e) => setFormData({ ...formData, code_alpha2: e.target.value.toUpperCase() })}
              maxLength={2}
              required
            />
            <Input
              label="Phone Code"
              value={formData.phone_code}
              onChange={(e) => setFormData({ ...formData, phone_code: e.target.value })}
              placeholder="+91"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Currency Code"
              value={formData.currency_code}
              onChange={(e) => setFormData({ ...formData, currency_code: e.target.value.toUpperCase() })}
              maxLength={3}
            />
            <Input
              label="Currency Symbol"
              value={formData.currency_symbol}
              onChange={(e) => setFormData({ ...formData, currency_symbol: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Timezone"
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            />
            <Input
              label="Tax Rate (%)"
              type="number"
              value={formData.tax_rate}
              onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) })}
            />
          </div>
          <Input
            label="Regulatory Body"
            value={formData.regulatory_body}
            onChange={(e) => setFormData({ ...formData, regulatory_body: e.target.value })}
            placeholder="e.g., IRDAI"
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="kyc_required"
              checked={formData.kyc_required}
              onChange={(e) => setFormData({ ...formData, kyc_required: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="kyc_required" className="text-sm text-gray-700">
              KYC Required
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingCountry ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Country"
      >
        <p className="text-gray-600">
          Are you sure you want to delete this country? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
